/**
 * docxRender.ts
 * Renders a filled DOCX (already marker-free) into per-page HTML fragments so
 * the existing html2canvas/modern-screenshot + pdf-lib pipeline can export each
 * physical Word page without clipping content or overlapping the footer.
 */

import { renderDocxPreview } from "@/services/docxService";
import { PAGE_MARKER, TOTAL_MARKER, PAGE_VAR, NUMPAGES_VAR } from "@/utils/docxMarker";
import {
  applyFieldLayoutRules,
  ensureFooterZone,
  pageLabel,
  type LayoutWarning,
} from "@/utils/fieldLayout";
import { inlineDomImages } from "@/utils/printExporter";
import type { PaperSize, RenderedPage } from "@/types/print";

const MM_PER_PX = 25.4 / 96;

const PAPER_MM: Record<PaperSize, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  A6: { width: 105, height: 148 },
  Letter: { width: 216, height: 279 },
  Legal: { width: 216, height: 356 },
  Thermal80: { width: 80, height: 297 },
  // Custom is detected from the section geometry; this is only a fallback.
  Custom: { width: 100, height: 150 },
};

function nearestPaperSize(widthMm: number, heightMm: number): PaperSize {
  let best: PaperSize = "A4";
  let bestDist = Infinity;
  (Object.keys(PAPER_MM) as PaperSize[]).forEach((key) => {
    const d = Math.abs(PAPER_MM[key].width - widthMm) + Math.abs(PAPER_MM[key].height - heightMm);
    if (d < bestDist) {
      bestDist = d;
      best = key;
    }
  });
  return best;
}

/**
 * A rendered page clips its overflow, so content taller than the paper is
 * silently cut. Comparing scroll height against client height surfaces that.
 */
export function isOverflowing(el: HTMLElement, tolerancePx = 2): boolean {
  const clientHeight = el.clientHeight;
  if (!clientHeight) return false;
  return el.scrollHeight > clientHeight + tolerancePx;
}

export interface DocxRenderOptions {
  fallbackPaper?: PaperSize;
  /** When true, measure each section's rendered size to pick the paper size. */
  detectPaper?: boolean;
}

export interface RenderDocxResult {
  pages: RenderedPage[];
  layoutWarnings: LayoutWarning[];
}

/**
 * Snapshot every `section.docx` in a live preview/export host into RenderedPage
 * fragments (styles inlined once). Used to reuse a finished preview for PDF
 * without running docx-preview again.
 */
export function snapshotDocxSections(
  container: HTMLElement,
  options: DocxRenderOptions = {}
): RenderedPage[] {
  const fallback = options.fallbackPaper ?? "A4";
  const sections = Array.from(container.querySelectorAll("section.docx")) as HTMLElement[];
  if (sections.length === 0) {
    throw new Error("Template Word không tạo ra nội dung có thể in.");
  }
  const styleTags = Array.from(container.querySelectorAll("style"))
    .map((st) => st.outerHTML)
    .join("\n");

  return sections.map((el, idx) => {
    let paperSize: PaperSize = fallback;
    let orientation: "portrait" | "landscape" = "portrait";
    if (options.detectPaper) {
      // Measure with display forced on — preview hides non-active sections.
      const prevDisplay = el.style.display;
      el.style.display = "block";
      const w = el.scrollWidth || el.offsetWidth || 794;
      const h = el.scrollHeight || el.offsetHeight || 1123;
      el.style.display = prevDisplay;
      const wMm = w * MM_PER_PX;
      const hMm = h * MM_PER_PX;
      paperSize = nearestPaperSize(wMm, hMm);
      orientation = hMm >= wMm ? "portrait" : "landscape";
    }
    // Clone without transform/scale from the live preview zoom.
    const clone = el.cloneNode(true) as HTMLElement;
    clone.style.transform = "";
    clone.style.transformOrigin = "";
    clone.style.display = "block";
    return {
      pageIndex: idx,
      html: (styleTags ? styleTags + "\n" : "") + clone.outerHTML,
      rowIndex: 0,
      templatePageIndex: 0,
      paperSize,
      orientation,
      overflow: isOverflowing(el),
    };
  });
}

/**
 * Render a filled DOCX buffer to per-page RenderedPage fragments.
 * Each `<section class="docx">` produced by docx-preview becomes one physical page.
 */
export async function renderDocxToPages(
  docxBuffer: ArrayBuffer | Uint8Array,
  options: DocxRenderOptions = {}
): Promise<RenderedPage[]> {
  const { pages } = await renderDocxToPagesWithWarnings(docxBuffer, options);
  return pages;
}

export async function renderDocxToPagesWithWarnings(
  docxBuffer: ArrayBuffer | Uint8Array,
  options: DocxRenderOptions = {}
): Promise<RenderDocxResult> {
  const container = document.createElement("div");
  container.style.cssText = "position:absolute;left:-20000px;top:0;width:1200px;";
  document.body.appendChild(container);
  try {
    await renderDocxPreview(docxBuffer, container);
    const layoutWarnings = applyFieldLayoutRules(container);
    // Bake blob:/http logos into data: URLs BEFORE snapshot — otherwise PDF/print
    // capture runs later when blob: URLs may already be revoked or invisible to
    // modern-screenshot foreignObject.
    await inlineDomImages(container);
    const pages = snapshotDocxSections(container, options);
    return { pages, layoutWarnings };
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Replace the footer page markers ⟦PAGE⟧ / ⟦TOTAL⟧ with the actual page number
 * and total across the whole export set (1-based, global numbering), and always
 * inject a visible "Trang i/N" footer inside the paper safe zone so the page
 * number is guaranteed to appear in PDF and print output.
 *
 * `pages` must already exclude sections dropped by display conditions — Y is
 * `pages.length` (actual pages after split + filter).
 */
export function applyPageNumbering(pages: RenderedPage[]): RenderedPage[] {
  const total = pages.length;
  return pages.map((p, i) => {
    let html = p.html
      .split(PAGE_MARKER)
      .join(String(i + 1))
      .split(TOTAL_MARKER)
      .join(String(total))
      .split(PAGE_VAR)
      .join(String(i + 1))
      .split(NUMPAGES_VAR)
      .join(String(total));

    const label = pageLabel(i + 1, total);
    if (typeof document !== "undefined") {
      const wrap = document.createElement("div");
      wrap.innerHTML = html;
      const section =
        (wrap.querySelector("section.docx") as HTMLElement | null) ??
        (wrap.firstElementChild as HTMLElement | null);
      if (section) {
        ensureFooterZone(section, label);
        html = wrap.innerHTML;
      } else if (!html.includes("docx-page-footer")) {
        html += footerHtml(label);
      }
    } else if (!html.includes("docx-page-footer")) {
      html += footerHtml(label);
    } else {
      html = html.replace(
        /(<div class="docx-page-footer"[^>]*>)([\s\S]*?)(<\/div>)/i,
        `$1${label}$3`
      );
    }
    return { ...p, html };
  });
}

function footerHtml(label: string): string {
  return `<div class="docx-page-footer" style="position:absolute;bottom:23px;left:0;right:0;text-align:center;font-size:10px;line-height:1.2;color:#444;z-index:5;height:14px;overflow:visible;">${label}</div>`;
}
